import React, { useCallback, useEffect, useState } from 'react';

import {
  consultarPosicaoAgora,
  obterPosicaoGravada,
  salvarImeiRastreador,
  salvarCpfCnpjRastreador,
  type ConsultaPosicao,
  type PosicaoRastreador,
  type VeiculoRastreado,
} from '../../../api/rastreamentoApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
import './RastreamentoVeiculoModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface RastreamentoVeiculoModalProps {
  veiculo: VeiculoRastreado | null;
  onClose: () => void;
  // Sem `sidebarRef`: o fechamento é pelo overlay (ver `fecharPeloOverlay`). Aceitar o ref
  // aqui reintroduziria a dependência que causou o BUG-R27.
}

const dataHora = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('pt-BR');
};

/**
 * A rastreadora manda a data **já em dd/mm/yyyy hh:mm:ss**, sem fuso (medido na API real em
 * 19/08/2026 — a doc deles mostra `yyyy-mm-dd`, que não é o que chega). Exibimos como veio,
 * apenas encurtando os segundos: converter para `Date` aqui inventaria um fuso e deslocaria o
 * horário que o rastreador compara com o relógio dele.
 */
const dataDaRastreadora = (valor?: string): string => {
  if (!valor) return '-';
  const m = valor.match(/^(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : valor;
};

const simNao = (valor?: string): string => {
  if (valor === 'S') return 'Sim';
  if (valor === 'N') return 'Não';
  return valor || '-';
};

/**
 * Link do mapa — SEMPRE o Google Maps (decisão do usuário, 20/08/2026): o campo `google` que a
 * própria posição traz, com fallback por latitude/longitude. A tela da rastreadora (link de
 * compartilhamento de 24h) saiu do fluxo — junto com a segunda chamada externa por clique, o
 * que importa porque a API deles tem blacklist por IP.
 */
const linkDoMapa = (posicao?: PosicaoRastreador | null): string | null => {
  if (!posicao) return null;
  if (posicao.google) return posicao.google;
  if (!posicao.latitude || !posicao.longitude) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(posicao.latitude)},${encodeURIComponent(posicao.longitude)}`;
};

/**
 * Modal do veículo na tela do RASTREADOR (at-76, ADR 0034).
 *
 * Abre ao clicar na placa e traz duas coisas: os dados do atendimento que a tabela já mostra e
 * a **posição na rastreadora**. A separação que governa o desenho:
 *
 *  - **abrir o modal NÃO consulta a rastreadora** — mostra a última consulta gravada, com a
 *    data dela. Consulta de geolocalização é ato explícito, nunca efeito de clique;
 *  - **"consultado há 3 dias" é informação, não detalhe**: sem a data, o rastreador leria uma
 *    posição velha como se fosse de agora — que é o mesmo erro de julgamento que a tela
 *    inteira existe para evitar.
 */
const RastreamentoVeiculoModal: React.FC<RastreamentoVeiculoModalProps> = ({ veiculo, onClose }) => {
  const [consulta, setConsulta] = useState<ConsultaPosicao | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [consultando, setConsultando] = useState(false);
  /** Editor inline do IMEI (ADR 0035). Abre sozinho quando não há IMEI cadastrado. */
  const [editandoImei, setEditandoImei] = useState(false);
  const [imeiDigitado, setImeiDigitado] = useState('');
  const [salvandoImei, setSalvandoImei] = useState(false);
  /**
   * Falha da ÚLTIMA consulta, com a origem. Fica na tela (o toast some em segundos e leva
   * junto a informação de quem falhou) — é o que evita o rastreador concluir que o sistema
   * da oficina quebrou quando quem recusou foi a rastreadora.
   */
  const [falhaConsulta, setFalhaConsulta] = useState<{
    mensagem: string;
    origem: 'RASTREADORA' | 'OFICINA';
    /**
     * Rótulos dos documentos que a cascata tentou antes de desistir (at-83).
     *
     * É o que transforma um beco sem saída em próximo passo: sem esta lista, "não localizado"
     * é indistinguível do erro genérico e o rastreador não descobre que pode informar o
     * documento do contrato. Vazio/ausente = falha que não passou pela cascata.
     */
    documentosTentados?: string[];
  } | null>(null);
  const [editandoDoc, setEditandoDoc] = useState(false);
  const [docDigitado, setDocDigitado] = useState('');
  const [salvandoDoc, setSalvandoDoc] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carId = veiculo?.id ?? null;

  const carregarGravada = useCallback(async (id: string) => {
    setCarregando(true);
    try {
      const dados = await obterPosicaoGravada(id);
      setConsulta(dados);
      setErro(null);
    } catch (error) {
      // Estado seguro + sinal visível (at-60). Texto FIXO, não `errorFrom`: o que importa aqui
      // é a CONSEQUÊNCIA (o que a tela pode estar escondendo), não o motivo HTTP — num 403 o
      // extrator devolveria "sem permissão", que não orienta ninguém (regra da at-65).
      setConsulta(null);
      setErro(
        'Não foi possível carregar a última consulta de posição deste veículo. O espaço abaixo ' +
          'estar vazio não significa que o veículo nunca foi consultado.',
      );
      notify.error(
        'Não foi possível carregar a última posição consultada. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'rastreamento-posicao-carga' },
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (carId) void carregarGravada(carId);
  }, [carId, carregarGravada]);

  /**
   * Fechamento por clique fora — pelo OVERLAY, não por handler global em `document`.
   *
   * ⚠️ Esta escolha é correção, não estilo (BUG-R27). O padrão herdado do sistema escuta
   * `mousedown` no `document` e pergunta "o alvo está fora do modal E fora da sidebar?",
   * consultando um `sidebarRef` que vem de fora. Isso torna o fechamento **refém de um ref
   * alheio**: a `RastreamentoPage` reatribuía esse mesmo ref ao próprio container, então
   * `sidebarRef.current` deixava de ser a sidebar e passava a ser a página inteira — e o
   * modal ficava impossível de fechar por clique fora, que foi o defeito relatado.
   *
   * Aqui o overlay É a região "fora": o clique só fecha quando o alvo é o próprio overlay,
   * e o cartão do modal, por estar dentro dele, nunca dispara. Não há ref externo envolvido,
   * então nenhuma mudança em outra tela pode voltar a quebrar isto.
   *
   * `mousedown` + conferência no `mouseup` (via `onClick`, que só dispara quando os dois
   * caem no mesmo elemento): selecionar texto dentro do modal e soltar o botão fora não fecha.
   */
  const fecharPeloOverlay = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  // Esc fecha — mesma tecla que o drawer "Como Funciona" já usa no resto do sistema.
  // Este modal não hospeda drawer nenhum, então não há a guarda de irmãos a fazer aqui.
  useEffect(() => {
    const aoTeclar = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  const salvarImei = async () => {
    if (!carId) return;
    const digitos = imeiDigitado.replace(/\D/g, '');
    if (digitos.length < 14 || digitos.length > 17) {
      notify.warning('O IMEI do rastreador deve ter de 14 a 17 dígitos.');
      return;
    }
    setSalvandoImei(true);
    try {
      const dados = await salvarImeiRastreador(carId, digitos);
      setConsulta(dados);
      setEditandoImei(false);
      setImeiDigitado('');
      // A alteração fica auditada no backend (quem/quando/de-para) — o toast só confirma.
      notify.success('IMEI do rastreador salvo.');
    } catch (error) {
      // Mutação que falhou: motivo real do backend, e NENHUM toast de sucesso (at-60).
      notify.errorFrom(error, 'Não foi possível salvar o IMEI do rastreador.');
    } finally {
      setSalvandoImei(false);
    }
  };

  /**
   * Salva o CPF/CNPJ do contrato de rastreamento (at-83, ADR 0037).
   *
   * Irmão do `salvarImei`, e com a mesma regra da at-60: mutação que falha mostra o motivo real
   * do backend e NÃO emite toast de sucesso.
   *
   * ⚠️ Salvar **não consulta**. Abrir ou salvar nunca dispara chamada externa (ADR 0034) — o
   * rastreador clica em "Consultar posição agora" em seguida, e é esse clique que vale.
   */
  const salvarDocumento = async () => {
    if (!carId) return;
    const digitos = docDigitado.replace(/\D/g, '');
    if (digitos.length !== 11 && digitos.length !== 14) {
      notify.warning('Informe um CPF (11 dígitos) ou um CNPJ (14 dígitos).');
      return;
    }
    setSalvandoDoc(true);
    try {
      const dados = await salvarCpfCnpjRastreador(carId, digitos);
      setConsulta(dados);
      setEditandoDoc(false);
      setDocDigitado('');
      // A falha anterior deixa de valer: o documento mudou, então a lista do que foi tentado
      // ficaria mentindo sobre o estado atual.
      setFalhaConsulta(null);
      notify.success('CPF/CNPJ do contrato salvo. Consulte a posição novamente.');
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível salvar o CPF/CNPJ do contrato de rastreamento.');
    } finally {
      setSalvandoDoc(false);
    }
  };

  const consultarAgora = async () => {
    if (!carId) return;
    setConsultando(true);
    setFalhaConsulta(null);
    const tinhaImei = consulta?.possuiImei === true;
    try {
      const dados = await consultarPosicaoAgora(carId);
      setConsulta(dados);
      setErro(null);

      /**
       * A transição `false → true` é a única evidência de que a descoberta automática rodou
       * (at-81) — do ponto de vista de quem clicou, um campo do cadastro mudou sozinho, e
       * mudança silenciosa em cadastro é o tipo de coisa que se descobre tarde e mal.
       *
       * Texto à mão, não `actionSuccess`: o helper conjuga a partir de um substantivo
       * masculino singular e produziria frase quebrada com "IMEI do rastreador".
       */
      if (!tinhaImei && dados.possuiImei) {
        notify.success(
          'Posição consultada. O IMEI do rastreador foi localizado pela placa e gravado no cadastro deste veículo.',
        );
      } else {
        notify.success('Posição consultada na rastreadora.');
      }
    } catch (error) {
      // Aqui SIM `errorFrom`: é uma AÇÃO que falhou, e o backend explica o que impediu —
      // informação que orienta o próximo passo. E nenhum toast de sucesso sai.
      notify.errorFrom(
        error,
        'Não foi possível consultar a posição na rastreadora. Tente novamente em instantes.',
      );

      // Além do toast, a falha FICA na tela com a origem — ver o comentário do estado.
      const corpo = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
      setFalhaConsulta({
        mensagem:
          typeof corpo?.message === 'string'
            ? corpo.message
            : 'Não foi possível consultar a posição na rastreadora.',
        origem: corpo?.origem === 'OFICINA' ? 'OFICINA' : 'RASTREADORA',
        documentosTentados: Array.isArray(corpo?.documentosTentados)
          ? (corpo?.documentosTentados as string[])
          : undefined,
      });
    } finally {
      setConsultando(false);
    }
  };

  if (!veiculo) return null;

  const posicao: PosicaoRastreador | null = consulta?.posicao ?? null;
  const mapa = linkDoMapa(posicao);

  return (
    <div className="rvm-overlay" onClick={fecharPeloOverlay}>
      <div className="rvm-modal" role="dialog" aria-label={`Veículo ${veiculo.plate}`}>
        <div className="rvm-header">
          <div>
            <div className="rvm-placa">{veiculo.plate}</div>
            <div className="rvm-subtitulo">
              {veiculo.model || 'Modelo não informado'} · {veiculo.carClass || '-'}
            </div>
          </div>
          <button type="button" className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        {erro && (
          <div className="erro-de-tela" role="alert">
            {erro}
          </div>
        )}

        <div className="rvm-secao">
          <div className="rvm-secao-titulo">Atendimento</div>
          <div className="rvm-grid">
            <div>
              <span className="rvm-rotulo">Proprietário</span>
              <span className="rvm-valor" title={veiculo.ownerName ?? ''}>
                {veiculo.ownerName || '-'}
              </span>
            </div>
            <div>
              <span className="rvm-rotulo">Situação</span>
              <span className="rvm-valor">
                {veiculo.entregue
                  ? `Saiu da oficina em ${dataHora(veiculo.saiuEm).split(',')[0]}`
                  : 'Na oficina'}
              </span>
            </div>
          </div>
        </div>

        <div className="rvm-secao">
          <div className="rvm-secao-titulo">Posição no rastreador</div>

          {carregando && <p className="rvm-hint">Carregando a última consulta...</p>}

          {!carregando && consulta?.integracaoConfigurada === false && (
            <p className="rvm-hint">
              A consulta de posição não está disponível neste ambiente. Acione a TI.
            </p>
          )}

          {/* ── AÇÃO PRIMEIRO ────────────────────────────────────────────────────────────
              O botão abre a seção (ajuste de UX pedido pelo Victor em 21/08/2026, no primeiro
              uso da at-81). Antes, o bloco de IMEI vinha no topo com um parágrafo inteiro, e a
              tela dava a impressão de EXIGIR o cadastro antes de consultar — exatamente o que a
              at-81 tinha acabado de remover. Quem abre este modal quer rastrear; o IMEI é
              detalhe de configuração e foi para o pé da seção, recolhido. */}
          {!carregando && (
            <>
              <button
                type="button"
                className="rvm-botao-consultar"
                onClick={consultarAgora}
                /**
                 * at-81: `possuiImei === false` SAIU daqui. Enquanto a busca por placa não
                 * funcionava na rastreadora, consultar sem IMEI era recusa certa e desabilitar
                 * poupava o clique; hoje é o contrário — o botão desabilitado esconderia a
                 * única forma de o veículo ganhar um IMEI. Falta de configuração do ambiente
                 * continua desabilitando: ali não há o que tentar.
                 *
                 * A guarda de `carregando` virou a montagem condicional acima: enquanto a carga
                 * não responde, `consulta` é `null` e toda guarda escrita como
                 * `consulta?.x === false` é falsa — o clique passava por cima do bloqueio de
                 * "integração não configurada" e faria a tela anunciar uma descoberta de IMEI
                 * que não aconteceu (não havia estado anterior para comparar).
                 */
                disabled={consultando || consulta?.integracaoConfigurada === false}
              >
                {consultando ? 'Consultando...' : 'Consultar posição agora'}
              </button>

              <p className="rvm-hint rvm-hint-privacidade">
                A consulta pergunta a posição atual à rastreadora e substitui a anterior, ficando
                registrada com o seu nome e a data. Consulte quando precisar responder onde o
                veículo está — não como rotina.
              </p>
            </>
          )}

          {!carregando && !posicao && consulta?.integracaoConfigurada !== false && !erro && (
            <p className="rvm-hint">Este veículo ainda não teve a posição consultada.</p>
          )}

          {posicao && (
            <>
              {/* A data da consulta vem PRIMEIRO, e em destaque: é ela que diz se o que está
                  abaixo vale como resposta ou é histórico. */}
              <div className="rvm-consulta-quando">
                Consultado em <strong>{dataHora(consulta?.consultadoEm ?? null)}</strong>
                {consulta?.consultadoPorNome ? ` por ${consulta.consultadoPorNome}` : ''}
              </div>

              {/* at-81 (BUG-V91). Fica ACIMA da posição, e é faixa e não toast: quem lê o
                  endereço precisa ler junto que ele pode não ser deste veículo. Sem isto, um
                  IMEI errado exibe a localização de outro carro sem nada denunciar. */}
              {consulta?.placaDivergente && (
                <div className="rvm-divergencia" role="alert">
                  <strong>Confira antes de usar esta posição.</strong> A rastreadora respondeu
                  sobre a placa <strong>{posicao.placa}</strong>, que não é a deste atendimento (
                  <strong>{veiculo.plate}</strong>). Provavelmente o IMEI cadastrado é de outro
                  veículo — corrija-o abaixo antes de informar esta localização.
                </div>
              )}

              {/* O ENDEREÇO é a resposta que o rastreador procura — coordenada é detalhe.
                  Vem pronto da rastreadora (campo não documentado, medido em 19/08/2026). */}
              {posicao.endereco && (
                <div className="rvm-endereco" title={posicao.endereco}>
                  {posicao.endereco}
                </div>
              )}

              <div className="rvm-grid">
                <div>
                  <span className="rvm-rotulo">Ignição</span>
                  <span className="rvm-valor">{simNao(posicao.ignicaoLigada)}</span>
                </div>
                <div>
                  <span className="rvm-rotulo">Em movimento</span>
                  <span className="rvm-valor">{simNao(posicao.movimento)}</span>
                </div>
                <div>
                  <span className="rvm-rotulo">Velocidade</span>
                  <span className="rvm-valor">
                    {posicao.velocidade ? `${posicao.velocidade} km/h` : '-'}
                  </span>
                </div>
                <div>
                  <span className="rvm-rotulo">Sinal do GPS</span>
                  <span className="rvm-valor">{posicao.statusGPS || '-'}</span>
                </div>
                <div>
                  <span className="rvm-rotulo">Última posição do GPS</span>
                  <span className="rvm-valor" title={dataDaRastreadora(posicao.dataGPS)}>
                    {dataDaRastreadora(posicao.dataGPS)}
                  </span>
                </div>
                <div>
                  <span className="rvm-rotulo">Comunicação</span>
                  <span
                    className="rvm-valor"
                    title={`${posicao.statusGPRS || '-'}${
                      posicao.dataGPRS ? ` (${dataDaRastreadora(posicao.dataGPRS)})` : ''
                    }`}
                  >
                    {posicao.statusGPRS || '-'}
                    {posicao.dataGPRS ? ` (${dataDaRastreadora(posicao.dataGPRS)})` : ''}
                  </span>
                </div>
              </div>

              {mapa ? null : (
                // Raro por construção (toda posição vem com coordenadas), mas "sem botão e sem
                // explicação" deixaria o rastreador achando que a tela quebrou.
                <p className="rvm-hint">
                  Esta consulta veio sem coordenadas — consulte novamente para obter o mapa.
                </p>
              )}

              {mapa && (
                <a className="rvm-link-mapa" href={mapa} target="_blank" rel="noopener noreferrer">
                  Ver no mapa
                </a>
              )}
            </>
          )}

          {falhaConsulta && (
            <div
              className={`rvm-falha rvm-falha--${falhaConsulta.origem === 'RASTREADORA' ? 'externa' : 'interna'}`}
              role="alert"
            >
              <span className="rvm-falha-origem">
                {falhaConsulta.origem === 'RASTREADORA'
                  ? 'Resposta da rastreadora (sistema externo)'
                  : 'Configuração do sistema da oficina'}
              </span>
              <span className="rvm-falha-mensagem">{falhaConsulta.mensagem}</span>
              {falhaConsulta.origem === 'RASTREADORA' && (
                <span className="rvm-falha-rodape">
                  Esta resposta veio do sistema da rastreadora — a oficina não controla essa
                  parte. A posição mostrada acima, se houver, é a da última consulta que deu
                  certo.
                </span>
              )}

              {/* ── O que já foi tentado, e o que fazer agora (at-83) ──────────────────────
                  Texto FIXO, não `errorFrom`: aqui a CONSEQUÊNCIA importa mais que o motivo
                  HTTP (regra da at-65). O motivo da rastreadora já está acima; o que falta é
                  dizer que o sistema esgotou o que sabia e que o próximo passo é do
                  rastreador. Sem isto, a falha parece o mesmo erro de antes. */}
              {falhaConsulta.documentosTentados && falhaConsulta.documentosTentados.length > 0 && (
                <span className="rvm-falha-tentativas">
                  A consulta foi tentada com: {falhaConsulta.documentosTentados.join(', ')}. Se o
                  contrato de rastreamento deste veículo estiver no CPF ou CNPJ de outra pessoa,
                  informe o documento abaixo e consulte novamente.
                </span>
              )}
            </div>
          )}

          {/* ── IMEI: uma LINHA, no pé, recolhido ────────────────────────────────────────
              Detalhe de configuração, não pré-requisito (at-81) — e, desde 21/08/2026, também
              não é mais um parágrafo no topo. O editor só aparece a pedido: sem IMEI, a
              consulta funciona pela placa, então oferecer o formulário aberto sugeria uma
              obrigação que não existe. */}
          {!carregando && consulta && consulta.integracaoConfigurada !== false && (
            <div className="rvm-imei-linha">
              {consulta.possuiImei ? (
                <>
                  <span className="rvm-rotulo">IMEI do rastreador</span>
                  <span className="rvm-imei-valor">{consulta.rastreadorImei}</span>
                </>
              ) : (
                <span className="rvm-imei-ausente">
                  Sem IMEI cadastrado — a busca é feita pela placa.
                </span>
              )}
              {!editandoImei && (
                <button
                  type="button"
                  className="rvm-imei-alterar"
                  onClick={() => {
                    setImeiDigitado(consulta.rastreadorImei ?? '');
                    setEditandoImei(true);
                  }}
                >
                  {consulta.possuiImei ? 'Alterar' : 'Cadastrar IMEI'}
                </button>
              )}
            </div>
          )}

          {!carregando && editandoImei && (
            <div className="rvm-imei-bloco">
              <p className="rvm-imei-explicacao">
                {consulta?.possuiImei
                  ? 'Muda qual equipamento a rastreadora consulta para este veículo. Use quando o rastreador for trocado.'
                  : 'Só é preciso quando a rastreadora não encontra o veículo pela placa. O número está no painel dela, em "IMEI do equipamento".'}{' '}
                A alteração fica registrada com o seu nome e a data.
              </p>
              <div className="rvm-imei-editor">
                <input
                  type="text"
                  inputMode="numeric"
                  className="rvm-imei-input"
                  placeholder="359510086137028"
                  aria-label="IMEI do rastreador"
                  value={imeiDigitado}
                  onChange={(e) => setImeiDigitado(e.target.value.replace(/\D/g, '').slice(0, 17))}
                />
                <button
                  type="button"
                  className="rvm-imei-salvar"
                  onClick={salvarImei}
                  disabled={salvandoImei}
                >
                  {salvandoImei ? 'Salvando...' : 'Salvar IMEI'}
                </button>
                <button
                  type="button"
                  className="rvm-imei-cancelar"
                  onClick={() => {
                    setEditandoImei(false);
                    setImeiDigitado('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* ── CPF/CNPJ do contrato: uma LINHA, no pé, recolhido (at-83) ─────────────────
              Mesma forma da linha do IMEI acima, e pelo mesmo motivo: é detalhe de
              configuração, não pré-requisito. A consulta tenta sozinha os documentos que
              conhece; este campo só importa quando todos falham. */}
          {!carregando && consulta && consulta.integracaoConfigurada !== false && (
            <div className="rvm-imei-linha">
              {consulta.possuiCpfCnpj ? (
                <>
                  <span className="rvm-rotulo">CPF/CNPJ do contrato</span>
                  <span className="rvm-imei-valor">{consulta.rastreadorCpfCnpj}</span>
                </>
              ) : (
                <span className="rvm-imei-ausente">
                  Sem CPF/CNPJ do contrato — a consulta usa os documentos padrão.
                </span>
              )}
              {!editandoDoc && (
                <button
                  type="button"
                  className="rvm-imei-alterar"
                  onClick={() => {
                    setDocDigitado(consulta.rastreadorCpfCnpj ?? '');
                    setEditandoDoc(true);
                  }}
                >
                  {consulta.possuiCpfCnpj ? 'Alterar' : 'Informar CPF/CNPJ'}
                </button>
              )}
            </div>
          )}

          {!carregando && editandoDoc && (
            <div className="rvm-imei-bloco">
              <p className="rvm-imei-explicacao">
                É o CPF ou CNPJ de quem contratou o rastreamento deste veículo — nem sempre é o
                proprietário. Aparece no painel da rastreadora, em "Dados do cliente". Informe
                quando a consulta não localizar o veículo com os documentos padrão. A alteração
                fica registrada com o seu nome e a data.
              </p>
              <div className="rvm-imei-editor">
                <input
                  type="text"
                  inputMode="numeric"
                  className="rvm-imei-input"
                  placeholder="13587783705"
                  aria-label="CPF/CNPJ do contrato de rastreamento"
                  value={docDigitado}
                  onChange={(e) => setDocDigitado(e.target.value.replace(/\D/g, '').slice(0, 14))}
                />
                <button
                  type="button"
                  className="rvm-imei-salvar"
                  onClick={salvarDocumento}
                  disabled={salvandoDoc}
                >
                  {salvandoDoc ? 'Salvando...' : 'Salvar CPF/CNPJ'}
                </button>
                <button
                  type="button"
                  className="rvm-imei-cancelar"
                  onClick={() => {
                    setEditandoDoc(false);
                    setDocDigitado('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RastreamentoVeiculoModal;
